import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { FamilyPlanTabComponent } from '../family-plan-tab/family-plan-tab.component';
import { FamilyPlanViewService } from '../../services/family-plan-view.service';
import type { FamilyPlanView } from '../../shared/types/family-plan-view.types';
import { RoleService } from '../../services/role.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Customer } from '../customer-table/customer-table.component';
import { CustomerInfoTabComponent } from '../customer-info-tab/customer-info-tab.component';
import { VehiclesListComponent } from '../vehicles-list/vehicles-list.component';
import {
  ServiceHistoryComponent,
  ServiceRecord,
} from '../service-history/service-history.component';
import { AppointmentsComponent } from '../appointments/appointments.component';
import { ReviewsComponent } from '../reviews/reviews.component';
import { DiscountsUsedComponent } from '../discounts-used/discounts-used.component';
import { CustomerSubscriptionsTabComponent } from '../customer-subscriptions-tab/customer-subscriptions-tab.component';
import {
  CustomerSubscription,
  CustomerSubscriptionService,
} from '../../services/customer-subscription.service';
import { CustomerPrepaidTabComponent } from '../customer-prepaid-tab/customer-prepaid-tab.component';
import {
  CustomerPrepaidPurchase,
  CustomerPrepaidService,
} from '../../services/customer-prepaid.service';
import { PdfService } from '../../services/pdf.service';
import { OrderReceiptService } from '../../services/order-receipt.service';
import { runInvoiceDownloadAndNotify } from '../../shared/utils/invoice-download-toast.util';

export interface TabItem {
  id: string;
  label: string;
  icon: string;
  active: boolean;
}

/**
 * Tab-based view for displaying and managing customer-related sections (Customer Info, Vehicles, Service History, Appointments, Reviews, Discounts Used).
 * Provides a horizontally scrollable tab bar and coordinates actions across child tab components, such as downloading invoices and propagating customer updates.
 * Key methods:
 *   - onTabClick(tabId: string): Activates the selected tab and updates the internal tab state.
 *   - scrollTabs(direction: 'left' | 'right'): Smooth-scrolls the tab list container left or right and updates scroll button visibility.
 *   - getActiveTab(): TabItem | undefined - Returns the currently active tab item.
 *   - onCustomerUpdated(): void - Emits the customerUpdated event to notify parent components of updates.
 *   - onDownloadInvoice(serviceRecord: ServiceRecord): void - Triggers invoice generation for a service record via PdfService and shows notifications via NotificationService.
 *   - private updateScrollButtons(): void - Checks container and list dimensions to determine whether left/right scroll controls should be enabled.
 * Important lifecycle hooks:
 *   - ngOnInit(): Initializes component state (currently no special initialization).
 *   - ngAfterViewInit(): Ensures scroll state is evaluated after the view initializes and sets up a scroll listener on the tab container to keep scroll controls in sync.
 * @component
 * @selector app-customer-tab-view
 * @example
 *   <app-customer-tab-view [customer]="sampleCustomer" (customerUpdated)="handleCustomerUpdated()"></app-customer-tab-view>
 * @input {Customer | null} customer - The customer model to display and operate on; can be null when no customer is selected.
 * @output {EventEmitter<void>} customerUpdated - Emitted when the customer's data has been updated by any child tab so the parent can react (e.g., refresh lists).
 */
@Component({
  selector: 'app-customer-tab-view',
  standalone: true,
  imports: [
    CommonModule,
    CustomerInfoTabComponent,
    VehiclesListComponent,
    ServiceHistoryComponent,
    AppointmentsComponent,
    ReviewsComponent,
    DiscountsUsedComponent,
    FamilyPlanTabComponent,
    CustomerSubscriptionsTabComponent,
    CustomerPrepaidTabComponent,
  ],
  templateUrl: './customer-tab-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./customer-tab-view.component.scss'],
})
export class CustomerTabViewComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() customer: Customer | null = null;

  /** "First Last" for the Subscriptions tab's cancel panel subtitle. */
  get customerDisplayName(): string {
    const first = (this.customer?.first_name || '').trim();
    const last = (this.customer?.last_name || '').trim();
    return [first, last].filter(Boolean).join(' ');
  }
  @Output() customerUpdated = new EventEmitter<void>();
  /** CWS-1881: a prepaid redemption row asked to open another customer's record. */
  @Output() openCustomer = new EventEmitter<string>();
  @ViewChild('tabListContainer', { static: false }) tabListContainer!: ElementRef;
  @ViewChild('tabList', { static: false }) tabList!: ElementRef;

  tabs: TabItem[] = [
    {
      id: 'customer-info',
      label: 'Customer Info',
      icon: 'assets/images/customer/user.svg',
      active: true,
    },
    { id: 'vehicles', label: 'Vehicles', icon: 'assets/images/customer/car.svg', active: false },
    {
      id: 'service-history',
      label: 'Service History',
      icon: 'assets/images/customer/history.svg',
      active: false,
    },
    {
      id: 'appointments',
      label: 'Appointments',
      icon: 'assets/images/customer/calendar-time.svg',
      active: false,
    },
    { id: 'reviews', label: 'Reviews', icon: 'assets/images/customer/stars.svg', active: false },
    {
      id: 'discounts-used',
      label: 'Discounts Used',
      icon: 'assets/images/customer/discount.svg',
      active: false,
    },
  ];

  activeTabId = 'customer-info';
  canScrollLeft = false;
  canScrollRight = false;

  /** The customer's family; the Family Plan tab shows only when they are in one, as primary or member. */
  familyView: FamilyPlanView | null = null;

  /**
   * CWS-1470: every membership the customer has held. The Subscriptions tab is
   * hidden for a customer who never had one, so the list is loaded up front
   * rather than when the tab is opened.
   */
  subscriptions: CustomerSubscription[] = [];

  /**
   * CWS-1833: every prepaid wash book the customer has bought. The Prepaid tab
   * sits next to Subscriptions and, like it, is hidden for a customer who never
   * bought one, so the list is loaded up front.
   */
  prepaidPurchases: CustomerPrepaidPurchase[] = [];

  private static readonly SUBSCRIPTIONS_TAB: TabItem = {
    id: 'subscriptions',
    label: 'Subscriptions',
    icon: 'assets/images/customer/subscription.svg',
    active: false,
  };

  private static readonly PREPAID_TAB: TabItem = {
    id: 'prepaid',
    label: 'Prepaid',
    icon: 'assets/images/customer/prepaid.svg',
    active: false,
  };

  private static readonly FAMILY_PLAN_TAB: TabItem = {
    id: 'family-plan',
    label: 'Family Plan',
    icon: 'assets/images/customer/family.svg',
    active: false,
  };

  private readonly destroy$ = new Subject<void>();
  private readonly customerChanged$ = new Subject<void>();
  /** Drops a family read that a newer one replaces; the other tabs' loads are left running. */
  private readonly familyReload$ = new Subject<void>();

  constructor(
    private pdfService: PdfService,
    private orderReceiptService: OrderReceiptService,
    private messageService: NotificationService,
    private familyPlanViewService: FamilyPlanViewService,
    private customerSubscriptionService: CustomerSubscriptionService,
    private customerPrepaidService: CustomerPrepaidService,
    private roleService: RoleService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['customer'];
    // A refreshed copy of the open customer (CWS-1925) must not blank the tabs or bounce the user
    // off the one they are on; only a different customer reloads them.
    if (change && (change.firstChange || change.previousValue?.id !== change.currentValue?.id)) {
      this.loadFamilyPlans();
      this.loadSubscriptions();
      this.loadPrepaidPurchases();
    }
  }

  ngOnDestroy(): void {
    this.customerChanged$.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Reads the family again without blanking it: an empty view would drop the tab and the user's place. */
  onFamilyPlanChanged(): void {
    this.refreshFamilyView();
    // A member edit also writes the customer record; let the host refresh the open customer (CWS-1925).
    this.customerUpdated.emit();
  }

  /**
   * The tab reloaded its own cards after a plan change and handed them up. Taking
   * the list as given avoids a second request, and avoids blanking `subscriptions`
   * — which would drop the tab and bounce the user back to Customer Info.
   */
  onSubscriptionsChanged(subscriptions: CustomerSubscription[]): void {
    this.subscriptions = subscriptions;
    this.syncSubscriptionsTab();
    // Starting a family changes the subscriptions; the Family Plan tab follows without a reopen.
    this.refreshFamilyView();
  }

  /**
   * Subscriptions are visible to Customers-tab managers (admin and Attendant
   * plus); the API enforces the same rule.
   */
  private loadSubscriptions(): void {
    this.subscriptions = [];
    this.syncSubscriptionsTab();

    const customerId = this.customer?.id;
    if (!customerId || !this.roleService.canManageCustomers()) {
      return;
    }
    this.customerSubscriptionService
      .getCustomerSubscriptions(String(customerId))
      .pipe(takeUntil(this.customerChanged$), takeUntil(this.destroy$))
      .subscribe({
        next: subscriptions => {
          this.subscriptions = subscriptions;
          this.syncSubscriptionsTab();
        },
        // The tab stays hidden if subscriptions can't load; the rest of customer
        // details still works.
        error: () => undefined,
      });
  }

  private syncSubscriptionsTab(): void {
    const index = this.tabs.findIndex(tab => tab.id === 'subscriptions');
    const shouldShow = this.subscriptions.length > 0;
    if (shouldShow && index === -1) {
      // Right after Vehicles, ahead of Prepaid and Family Plan whichever list loaded first.
      const vehiclesIndex = this.tabs.findIndex(tab => tab.id === 'vehicles');
      this.tabs.splice(vehiclesIndex + 1, 0, {
        ...CustomerTabViewComponent.SUBSCRIPTIONS_TAB,
      });
    } else if (!shouldShow && index !== -1) {
      this.tabs.splice(index, 1);
      if (this.activeTabId === 'subscriptions') {
        this.onTabClick('customer-info');
      }
    }
    setTimeout(() => this.updateScrollButtons(), 0);
  }

  /**
   * CWS-1833: the tab reloaded its own cards after a cancellation and handed them up. Taking
   * the list as given avoids a second request, and avoids blanking `prepaidPurchases`, which
   * would drop the tab and bounce the user back to Customer Info.
   */
  onPrepaidPurchasesChanged(purchases: CustomerPrepaidPurchase[]): void {
    this.prepaidPurchases = purchases;
    this.syncPrepaidTab();
  }

  /** Prepaid books are visible to Customers-tab managers; the API enforces the same rule. */
  private loadPrepaidPurchases(): void {
    this.prepaidPurchases = [];
    this.syncPrepaidTab();

    const customerId = this.customer?.id;
    if (!customerId || !this.roleService.canManageCustomers()) {
      return;
    }
    this.customerPrepaidService
      .getCustomerPrepaidPurchases(String(customerId))
      .pipe(takeUntil(this.customerChanged$), takeUntil(this.destroy$))
      .subscribe({
        next: purchases => {
          this.prepaidPurchases = purchases;
          this.syncPrepaidTab();
        },
        // The tab stays hidden if the books can't load; the rest of customer details still works.
        error: () => undefined,
      });
  }

  private syncPrepaidTab(): void {
    const index = this.tabs.findIndex(tab => tab.id === 'prepaid');
    const shouldShow = this.prepaidPurchases.length > 0;
    if (shouldShow && index === -1) {
      // After Subscriptions when it is shown, else after Vehicles: the order is
      // Subscriptions, Prepaid, Family Plan regardless of which list answered first.
      const anchor = this.tabs.findIndex(tab => tab.id === 'subscriptions');
      const vehiclesIndex = this.tabs.findIndex(tab => tab.id === 'vehicles');
      this.tabs.splice((anchor === -1 ? vehiclesIndex : anchor) + 1, 0, {
        ...CustomerTabViewComponent.PREPAID_TAB,
      });
    } else if (!shouldShow && index !== -1) {
      this.tabs.splice(index, 1);
      if (this.activeTabId === 'prepaid') {
        this.onTabClick('customer-info');
      }
    }
    setTimeout(() => this.updateScrollButtons(), 0);
  }

  /** Reading a family is open to every role that can open this view; the tab shows for any member. */
  private loadFamilyPlans(): void {
    this.customerChanged$.next();
    this.familyView = null;
    this.syncFamilyPlanTab();
    this.refreshFamilyView();
  }

  /** Reads the family again; the tab keeps showing what it has until the answer arrives. */
  private refreshFamilyView(): void {
    this.familyReload$.next();

    const customerId = this.customer?.id;
    if (!customerId) {
      return;
    }
    this.familyPlanViewService
      .getForCustomer(String(customerId))
      .pipe(
        takeUntil(this.familyReload$),
        takeUntil(this.customerChanged$),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: view => {
          this.familyView = view;
          this.syncFamilyPlanTab();
        },
        // The tab stays hidden if the family can't load; the rest of customer details still works.
        error: () => undefined,
      });
  }

  private syncFamilyPlanTab(): void {
    const index = this.tabs.findIndex(tab => tab.id === 'family-plan');
    const shouldShow = this.familyView?.in_family === true;
    if (shouldShow && index === -1) {
      // After Prepaid, else Subscriptions, else Vehicles: the order is Subscriptions,
      // Prepaid, Family Plan regardless of which list answered first.
      const anchor = ['prepaid', 'subscriptions']
        .map(id => this.tabs.findIndex(tab => tab.id === id))
        .find(index => index !== -1);
      const vehiclesIndex = this.tabs.findIndex(tab => tab.id === 'vehicles');
      this.tabs.splice((anchor ?? vehiclesIndex) + 1, 0, {
        ...CustomerTabViewComponent.FAMILY_PLAN_TAB,
      });
    } else if (!shouldShow && index !== -1) {
      this.tabs.splice(index, 1);
      if (this.activeTabId === 'family-plan') {
        this.onTabClick('customer-info');
      }
    }
    setTimeout(() => this.updateScrollButtons(), 0);
  }

  /**
   * Update scroll button visibility after the component view initializes and attach a scroll event listener to the tab list container.
   * @example
   * gAfterViewInit()
   * // undefined
   * @returns {void} No return value; updates scroll controls and attaches a scroll listener to the tab list container.
   **/
  ngAfterViewInit(): void {
    // Check scroll state after view init
    setTimeout(() => {
      this.updateScrollButtons();
    }, 100);

    // Add scroll event listener to update buttons when user scrolls manually
    if (this.tabListContainer) {
      this.tabListContainer.nativeElement.addEventListener('scroll', () => {
        this.updateScrollButtons();
      });
    }
  }

  onTabClick(tabId: string) {
    this.activeTabId = tabId;
    this.tabs.forEach(tab => {
      tab.active = tab.id === tabId;
    });
  }

  /**
   * Smoothly scrolls the tab list container left or right and updates scroll buttons.
   * @example
   * crollTabs('left')
   * undefined
   * @param {{'left' | 'right'}} {{direction}} - Direction to scroll the tab list: 'left' or 'right'.
   * @returns {{void}} No return value; performs scrolling side-effects.
   **/
  scrollTabs(direction: 'left' | 'right'): void {
    if (!this.tabListContainer) {
      return;
    }

    const container = this.tabListContainer.nativeElement;
    const scrollAmount = 200; // Adjust scroll amount as needed

    if (direction === 'left') {
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    } else {
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }

    // Update scroll buttons after scrolling
    setTimeout(() => {
      this.updateScrollButtons();
    }, 300);
  }

  /**
   * Update visibility flags for horizontal scroll buttons (canScrollLeft and canScrollRight) based on the current scroll position and content width of the tab list container. Does nothing if required DOM references are missing.
   * @example
   * updateScrollButtons()
   * // sets this.canScrollLeft and this.canScrollRight; returns nothing
   * @returns {{void}} No return value; updates component state (this.canScrollLeft and this.canScrollRight).
   **/
  private updateScrollButtons(): void {
    if (!this.tabListContainer || !this.tabList) {
      return;
    }

    const container = this.tabListContainer.nativeElement;
    const list = this.tabList.nativeElement;

    // Check if we can scroll left (content overflows on the left)
    this.canScrollLeft = container.scrollLeft > 0;

    // Check if we can scroll right (content overflows on the right)
    const maxScrollLeft = list.scrollWidth - container.clientWidth;
    this.canScrollRight = container.scrollLeft < maxScrollLeft - 1; // Small tolerance for floating point
  }

  getActiveTab(): TabItem | undefined {
    return this.tabs.find(tab => tab.active);
  }

  onCustomerUpdated(): void {
    // Pass the customer updated event up to the parent component
    this.customerUpdated.emit();
  }

  /**
   * Trigger PDF invoice generation and download for a given service record and notify the user.
   * @example
   * nDownloadInvoice(exampleServiceRecord)
   * undefined
   * @param {ServiceRecord} serviceRecord - The service record to generate and download an invoice for.
   * @returns {void} Does not return a value; initiates invoice PDF creation, download, and a notification.
   **/
  onDownloadInvoice(serviceRecord: ServiceRecord): void {
    if (!this.customer) return;

    const customerName = `${this.customer.first_name}${this.customer.last_name ? ' ' + this.customer.last_name : ''}`;

    runInvoiceDownloadAndNotify(this.messageService, () => {
      this.pdfService.generateServiceHistoryInvoice(serviceRecord, {
        name: customerName,
        email: this.customer!.email,
        phone: this.customer!.contact,
        address: 'Sanger, Sanger, California, 93657',
      });
    });
  }

  /**
   * Prints the receipt for a past order through the same flow as checkout and order details
   * (network receipt printer first, PDF fallback).
   */
  onPrintInvoice(serviceRecord: ServiceRecord): void {
    // `id` is the order primary key; `orderId` is the `#…` display number the order API rejects.
    this.orderReceiptService.printReceiptForOrderId(serviceRecord.id, {
      paymentIntentId: serviceRecord.paymentIntentId,
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
